import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, doc, addDoc, onSnapshot, deleteDoc } from 'firebase/firestore';

// Graduation Requirements Management Hook
export const useGraduationRequirements = ({ currentUser }) => {
  const [graduationRequirements, setGraduationRequirements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      console.log('No current user, skipping graduation requirements fetch');
      return;
    }

    console.log('Setting up graduation requirements listener for user:', currentUser.uid);

    // Set a timeout to ensure loading doesn't get stuck
    const timeoutId = setTimeout(() => {
      console.log('Timeout reached, setting loading to false');
      setLoading(false);
    }, 5000); // 5 second timeout

    // Use a simpler query without ordering to avoid issues with empty collections
    const q = collection(db, 'graduationRequirements');

    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log('Graduation requirements snapshot received, size:', snapshot.size);
      clearTimeout(timeoutId); // Clear timeout since we got data
      const requirements = [];
      snapshot.forEach((doc) => {
        requirements.push({ id: doc.id, ...doc.data() });
      });
      // Sort in memory instead of using Firestore ordering
      requirements.sort((a, b) => {
        if (a.studentUid !== b.studentUid) {
          return a.studentUid.localeCompare(b.studentUid);
        }
        return a.requirementType.localeCompare(b.requirementType);
      });
      console.log('Processed graduation requirements:', requirements);
      setGraduationRequirements(requirements);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching graduation requirements:', error);
      clearTimeout(timeoutId); // Clear timeout since we got an error
      // If the collection doesn't exist or there's an error, just set empty array
      setGraduationRequirements([]);
      setLoading(false);
    });

    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [currentUser]);

  const addGraduationRequirement = async (studentUid, requirementType, completionDate = null) => {
    try {
      const docRef = await addDoc(collection(db, 'graduationRequirements'), {
        studentUid,
        requirementType,
        completionDate: completionDate || new Date(),
        certifiedBy: currentUser.uid,
        certifiedAt: new Date(),
        createdAt: new Date()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error adding graduation requirement:', error);
      throw error;
    }
  };

  const removeGraduationRequirement = async (requirementId) => {
    try {
      await deleteDoc(doc(db, 'graduationRequirements', requirementId));
    } catch (error) {
      console.error('Error removing graduation requirement:', error);
      throw error;
    }
  };

  return {
    graduationRequirements,
    loading,
    addGraduationRequirement,
    removeGraduationRequirement
  };
};

// Graduation Requirements Display Component
export const GraduationRequirementsDisplay = ({ 
  students, 
  graduationRequirements, 
  loading, 
  addGraduationRequirement, 
  removeGraduationRequirement,
  selectedStudentUid,
  setSelectedStudentUid 
}) => {
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Define the graduation requirements
  const requirementTypes = [
    'Core Classes, 9th grade',
    'Community Service (40 hours per year), 9th grade',
    'Physical Activity (100 hours per year), 9th grade',
    'Leadership Roles, 9th grade',
    'Summer Intensive, 9th grade',
    'Core Classes, 10th grade',
    'Community Service (40 hours per year), 10th grade',
    'Physical Activity (100 hours per year), 10th grade',
    'Leadership Roles, 10th grade',
    'Summer Intensive, 10th grade',
    'Core Classes, 11th grade',
    'Community Service (40 hours per year), 11th grade',
    'Physical Activity (100 hours per year), 11th grade',
    'Leadership Roles, 11th grade',
    'Summer Intensive, 11th grade',
    'Core Classes, 12th grade',
    'Community Service (40 hours per year), 12th grade',
    'Physical Activity (100 hours per year), 12th grade',
    'Leadership Roles, 12th grade',
    'Summer Intensive, 12th grade',
    'Mastery in all Core Competencies',
    'Mastery in Two or More Advanced Competencies',
    'Final Portfolio Defense: STEM Research',
    'Final Portfolio Defense: Non-STEM Research',
    'Final Portfolio Defense: Entrepreneurship',
    'Final Portfolio Defense: Service',
    'Final Portfolio Defense: Creative',
    'Minimum 4 Dual Enrollment Classes',
    '80 hours Work Experience',
    'Cultural Proficiency/World Languages',
  ];

  useEffect(() => {
    if (selectedStudentUid && selectedStudentUid !== 'all') {
      const student = students.find(s => s.id === selectedStudentUid);
      setSelectedStudent(student);
    } else {
      setSelectedStudent(null);
    }
  }, [selectedStudentUid, students]);

  // Removed unused getStudentRequirements to resolve linter warning

  const isRequirementCompleted = (studentUid, requirementType) => {
    return graduationRequirements.some(req => 
      req.studentUid === studentUid && 
      req.requirementType === requirementType
    );
  };

  const handleToggleRequirement = async (studentUid, requirementType) => {
    const isCompleted = isRequirementCompleted(studentUid, requirementType);
    
    if (isCompleted) {
      // Find and remove the requirement
      const requirement = graduationRequirements.find(req => 
        req.studentUid === studentUid && 
        req.requirementType === requirementType
      );
      if (requirement) {
        await removeGraduationRequirement(requirement.id);
      }
    } else {
      // Add the requirement
      await addGraduationRequirement(studentUid, requirementType);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date.seconds ? date.seconds * 1000 : date).toLocaleDateString();
  };

  if (loading) {
    return <div className="loading">Loading graduation requirements...</div>;
  }

  return (
    <div className="graduation-requirements-container">


      {/* Student Selection */}
      <div className="add-tag-section">
        <h3>Select Student</h3>
        <select
          value={selectedStudentUid}
          onChange={(e) => setSelectedStudentUid(e.target.value)}
          className="student-dropdown"
        >
          <option value="all">Choose a student...</option>
          {students.map(student => (
            <option key={student.id} value={student.id}>
              {student.firstName} {student.lastName}
            </option>
          ))}
        </select>
      </div>

      {/* Requirements for Selected Student */}
      {selectedStudent && (
        <div className="add-tag-section">
          <h3>Requirements for {selectedStudent.firstName} {selectedStudent.lastName}</h3>
          <div className="requirements-list">
            {requirementTypes.map(requirementType => {
              const isCompleted = isRequirementCompleted(selectedStudent.id, requirementType);
              const completion = graduationRequirements.find(req => 
                req.studentUid === selectedStudent.id && 
                req.requirementType === requirementType && 
                !req.removedAt
              );

              return (
                <div 
                  key={requirementType} 
                  className={`requirement-item ${isCompleted ? 'completed' : ''}`}
                  onClick={() => handleToggleRequirement(selectedStudent.id, requirementType)}
                >
                  <div className="requirement-info">
                    <div className="requirement-text">{requirementType}</div>
                    {completion ? (
                      <div className="completion-details">
                        <small>
                          Certified: {formatDate(completion.certifiedAt)}
                        </small>
                      </div>
                    ) : (
                      <div className="completion-details">
                        <small>Not Complete</small>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Graduation Requirements Transcript Display Component
export const GraduationRequirementsTranscriptDisplay = ({ 
  graduationRequirements, 
  selectedStudentUid,
  requirementTypes
}) => {
  const getStudentRequirements = (studentUid) => {
    return graduationRequirements.filter(req => 
      req.studentUid === studentUid
    );
  };

  const consolidateRequirements = (requirements) => {
    const consolidated = {};
    
    requirements.forEach(req => {
      // Handle both grade-level requirements and colon-separated types (like Final Portfolio Defense)
      let baseType = req.requirementType;
      let badge = null;
      
      // Check for grade pattern
      const gradeMatch = req.requirementType.match(/, (\d+)th grade$/);
      if (gradeMatch) {
        baseType = req.requirementType.replace(/, \d+th grade$/, '');
        badge = `${gradeMatch[1]}th Grade`;
      } else {
        // Check for colon pattern (e.g., "Final Portfolio Defense: STEM Research")
        const colonMatch = req.requirementType.match(/^([^:]+): (.+)$/);
        if (colonMatch) {
          baseType = colonMatch[1];
          badge = colonMatch[2];
        }
      }
      
      if (!consolidated[baseType]) {
        consolidated[baseType] = {
          baseType,
          badges: [],
          earliestDate: req.certifiedAt,
          latestDate: req.certifiedAt
        };
      }
      
      // Add badge if present
      if (badge) {
        consolidated[baseType].badges.push(badge);
      }
      
      // Update date range
      const reqDate = new Date(req.certifiedAt.seconds ? req.certifiedAt.seconds * 1000 : req.certifiedAt);
      const earliestDate = new Date(consolidated[baseType].earliestDate.seconds ? consolidated[baseType].earliestDate.seconds * 1000 : consolidated[baseType].earliestDate);
      const latestDate = new Date(consolidated[baseType].latestDate.seconds ? consolidated[baseType].latestDate.seconds * 1000 : consolidated[baseType].latestDate);
      
      if (reqDate < earliestDate) {
        consolidated[baseType].earliestDate = req.certifiedAt;
      }
      if (reqDate > latestDate) {
        consolidated[baseType].latestDate = req.certifiedAt;
      }
    });
    
    // Sort consolidated requirements by the order they appear in requirementTypes
    const consolidatedArray = Object.values(consolidated);
    return consolidatedArray.sort((a, b) => {
      const aIndex = requirementTypes.findIndex(type => {
        const stripped = type.replace(/, \d+th grade$/, '').replace(/: .+$/, '');
        return stripped === a.baseType;
      });
      const bIndex = requirementTypes.findIndex(type => {
        const stripped = type.replace(/, \d+th grade$/, '').replace(/: .+$/, '');
        return stripped === b.baseType;
      });
      return aIndex - bIndex;
    });
  };

  const studentReqs = getStudentRequirements(selectedStudentUid);
  
  if (studentReqs.length === 0) {
    return <p className="no-posts no-posts-padding">No graduation requirements completed yet.</p>;
  }

  const consolidatedReqs = consolidateRequirements(studentReqs);

  return (
    <div>
      <div className="transcript-grade-section">
        <h4 className="transcript-grade-header">
          Graduation Requirements
        </h4>
        
        <div className="transcript-time-logs-list">
          {consolidatedReqs.map((consolidated, index) => (
            <div key={index} className="transcript-time-log-entry">
              <div className="transcript-time-log-main">
                <h5 className="transcript-time-log-activity">
                  {consolidated.baseType}
                </h5>
                {consolidated.badges.length > 0 && (
                  <span className="transcript-grade-badge">
                    {consolidated.badges.sort((a, b) => {
                      // Sort grades numerically if they contain "Grade"
                      const aNum = a.match(/(\d+)th Grade/);
                      const bNum = b.match(/(\d+)th Grade/);
                      if (aNum && bNum) {
                        return parseInt(aNum[1]) - parseInt(bNum[1]);
                      }
                      
                      // For non-grade badges, maintain original order from requirementTypes
                      const aIndex = requirementTypes.findIndex(type => type.includes(`: ${a}`));
                      const bIndex = requirementTypes.findIndex(type => type.includes(`: ${b}`));
                      
                      if (aIndex !== -1 && bIndex !== -1) {
                        return aIndex - bIndex;
                      }
                      
                      return a.localeCompare(b);
                    }).join(', ')}
                  </span>
                )}
              </div>
              <div className="transcript-time-log-summary">
                <span className="transcript-time-log-hours">Completed</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
